import { type VoidAsyncResult, createPendingAsyncResult, createResolvedAsyncResult } from "@/cache/AsyncResult";
import type { ScenePublicApi } from "@/layers/Compositor";
import { type CheckedProps, LayerBase, type PropsChecker } from "@/layers/LayerBase";
import { clearLayerLoading, reportLayerLoading } from "@/layers/geojson/layerLoadingStore";
import { TEXT_BACKGROUND_IMG, loadTextBackgroundImage } from "@/layers/geojson/poi/PoiLayerUtilities/poi-mapbox-utils";
import {
  MapboxLayerGroup,
  MapboxSourceGroup,
  addLayerGroup,
  addSource,
  moveZIndex,
  removeLayerGroup,
  removeSource,
  updateMapboxLayoutProperties,
  updateMapboxPaintProperties,
  updateSourceData,
} from "@/layers/utility/mapbox-utils";
import { type ResolvedPortfolioEnergyAsset, resolvePortfolioEnergyAssets } from "@/portfolio/energyRequests";
import {
  type PortfolioEnergyForecastLoad,
  type PortfolioEnergyForecasts,
  type PortfolioEnergyValue,
  type PortfolioEnergyValueLoad,
  loadPortfolioEnergyForecasts,
  loadPortfolioEnergyValues,
  portfolioEnergyForecastRequestKey,
  portfolioEnergyValueRequestKey,
  selectPortfolioEnergyForecastValues,
} from "@/portfolio/energyValues";
import { type Portfolio, PortfolioDataSource, type PortfolioRow } from "@/portfolio/types";
import type { RenderablePortfolioEnergyPoiLayer } from "@/reducer/client-models/PoiLayer";
import { t } from "@lingui/core/macro";
import type { GuiTimeZone } from "@mm/metx-workbench.meteomatics.com";
import type { DateTime } from "luxon";
import mapboxgl, { type MapLayerMouseEvent, type MapboxGeoJSONFeature } from "mapbox-gl";
import { createPortfolioEnergyPopover } from "../popovers";
import "../style/mapboxTablePopover.scss";
import {
  type PortfolioEnergyFeatureProperties,
  portfolioEnergyFeatureId,
  projectPortfolioEnergyAssets,
} from "./projection";

type LayerName = "cluster" | "cluster-count" | "marker" | "icon" | "mw" | "name";

const unclustered = ["!", ["has", "point_count"]] as mapboxgl.Expression;
const windIcon = "mm-turbine";
const solarIcon = "mm-solar_collector";
const labelBackgroundLayout: mapboxgl.SymbolLayout = {
  "icon-ignore-placement": true,
  "icon-allow-overlap": true,
  "icon-text-fit": "both",
  "icon-text-fit-padding": [2, 10, 2, 10],
};

function opacity(props: RenderablePortfolioEnergyPoiLayer) {
  return props.show ? props.opacity : 0;
}

function createLayerGroup(id: string, source: string, props: RenderablePortfolioEnergyPoiLayer) {
  const visible = props.show ? "visible" : "none";
  const markerScale = props.poiOptions.display.markerSize * 4;
  const errorLabel = t`Error`;
  return new MapboxLayerGroup<LayerName>(id, [
    {
      layerName: "cluster",
      type: "circle",
      source,
      filter: ["has", "point_count"],
      layout: { visibility: visible },
      paint: {
        "circle-color": "#006991",
        "circle-radius": ["step", ["get", "point_count"], 17, 25, 21, 100, 25],
        "circle-opacity": opacity(props),
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 2,
      },
    },
    {
      layerName: "cluster-count",
      type: "symbol",
      source,
      filter: ["has", "point_count"],
      layout: {
        visibility: visible,
        "text-field": ["get", "point_count_abbreviated"],
        "text-size": 12,
      },
      paint: { "text-color": "#ffffff", "text-opacity": opacity(props) },
    },
    {
      layerName: "marker",
      type: "circle",
      source,
      filter: unclustered,
      layout: { visibility: visible },
      paint: {
        "circle-radius": markerScale * 11,
        "circle-color": [
          "match",
          ["get", "valueState"],
          "invalid-configuration",
          "#c54132",
          "failed",
          "#c54132",
          "missing",
          "#8c969b",
          "stale",
          "#8c969b",
          "#ffffff",
        ],
        "circle-opacity": opacity(props),
        "circle-stroke-color": "#ffffff",
        "circle-stroke-width": 1,
      },
    },
    {
      layerName: "icon",
      type: "symbol",
      source,
      filter: unclustered,
      layout: {
        visibility: visible,
        "icon-image": ["match", ["get", "technology"], "solar", solarIcon, windIcon],
        "icon-size": markerScale,
        "icon-allow-overlap": true,
      },
      paint: {
        "icon-color": ["match", ["get", "technology"], "solar", "#f0a000", "#006991"],
        "icon-opacity": opacity(props),
      },
    },
    {
      layerName: "mw",
      type: "symbol",
      source,
      filter: unclustered,
      layout: {
        visibility: visible,
        "icon-image": props.poiOptions.display.showMwValues
          ? [
              "case",
              ["==", ["get", "valueState"], "loaded"],
              TEXT_BACKGROUND_IMG,
              ["==", ["get", "valueState"], "failed"],
              TEXT_BACKGROUND_IMG,
              "",
            ]
          : "",
        ...labelBackgroundLayout,
        "text-field": props.poiOptions.display.showMwValues
          ? [
              "case",
              ["==", ["get", "valueState"], "loaded"],
              ["concat", ["to-string", ["/", ["round", ["*", ["get", "valueMw"], 100]], 100]], " MW"],
              ["==", ["get", "valueState"], "failed"],
              errorLabel,
              "",
            ]
          : "",
        "text-size": 12,
        "text-offset": [0, -4],
        "text-anchor": "top",
      },
      paint: {
        "icon-opacity": opacity(props),
        "text-color": "#003652",
        "text-opacity": opacity(props),
      },
    },
    {
      layerName: "name",
      type: "symbol",
      source,
      filter: unclustered,
      layout: {
        visibility: visible,
        "icon-image": props.poiOptions.display.showAssetNames
          ? ["case", ["!=", ["get", "name"], ""], TEXT_BACKGROUND_IMG, ""]
          : "",
        ...labelBackgroundLayout,
        "text-field": props.poiOptions.display.showAssetNames ? ["get", "name"] : "",
        "text-size": 12,
        "text-offset": [0, 3],
        "text-anchor": "top",
      },
      paint: {
        "icon-opacity": opacity(props),
        "text-color": "#003652",
        "text-opacity": opacity(props),
      },
    },
  ]);
}

export class PortfolioEnergyPoiLayerImpl extends LayerBase<RenderablePortfolioEnergyPoiLayer> {
  private readonly sourceGroup: MapboxSourceGroup<"assets">;
  private layerGroup: MapboxLayerGroup<LayerName>;
  private popover = new mapboxgl.Popup({
    maxWidth: "min(90vw, 900px)",
    className: "mapbox-popover mapbox-popover-table",
  });
  private portfolio?: Portfolio;
  private resolvedAssets: ResolvedPortfolioEnergyAsset[] = [];
  private requestFingerprint = "";
  private values = new Map<string, PortfolioEnergyValue>();
  private valueLoad?: PortfolioEnergyValueLoad;
  private activeValueKey?: string;
  private forecasts: PortfolioEnergyForecasts = new Map();
  private forecastLoad?: PortfolioEnergyForecastLoad;
  private activeForecastKey?: string;
  private interactionsBlocked: boolean;
  private rowsByFeatureId = new Map<string, PortfolioRow>();
  private rowsByAssetId = new Map<string, PortfolioRow>();

  constructor(id: number, props: RenderablePortfolioEnergyPoiLayer, scene: ScenePublicApi, timezone: GuiTimeZone) {
    super(id, props, scene, timezone);
    loadTextBackgroundImage(this.scene.getMapboxMap());
    this.interactionsBlocked = scene.arePortfolioEnergyInteractionsBlocked();
    this.sourceGroup = new MapboxSourceGroup(this.humanReadableId(), ["assets"]);
    this.layerGroup = this.newLayerGroup();
    this.addMapboxContent();
    this.addEventListeners();
    this.setPortfolio(props.portfolio);
  }

  private humanReadableId() {
    return `metx.PortfolioEnergyPoiLayer#${this.uid}`;
  }

  private newLayerGroup() {
    return createLayerGroup(this.humanReadableId(), this.sourceGroup.id("assets"), this.props);
  }

  private addMapboxContent() {
    const map = this.scene.getMapboxMap();
    addSource(map, [
      {
        sourceId: this.sourceGroup.id("assets"),
        source: {
          type: "geojson",
          data: { type: "FeatureCollection", features: [] },
          cluster: this.props.poiOptions.display.clusteringEnabled,
          clusterRadius: 50,
          clusterMaxZoom: 8,
        },
      },
    ]);
    addLayerGroup(map, this.layerGroup.initialSpecs());
  }

  private rebuildMapboxContent() {
    const map = this.scene.getMapboxMap();
    this.removeEventListeners();
    removeLayerGroup(map, this.layerGroup.allIds());
    removeSource(map, this.sourceGroup.allIds());
    this.layerGroup = this.newLayerGroup();
    this.addMapboxContent();
    this.addEventListeners();
    this.updateFeatures();
  }

  private setPortfolio(portfolio: Portfolio | undefined) {
    this.portfolio = portfolio;
    const resolved = portfolio ? resolvePortfolioEnergyAssets(portfolio) : undefined;
    const includedAssetIds = this.props.poiOptions.includedAssetIds;
    this.resolvedAssets = (resolved?.assets ?? []).filter((asset) =>
      asset.assetId ? !includedAssetIds || includedAssetIds.includes(asset.assetId) : false,
    );
    this.requestFingerprint = resolved?.requestFingerprint ?? "";
    if (!portfolio) {
      this.stopValueLoad();
      this.values.clear();
      this.activeValueKey = undefined;
      this.forecasts = new Map();
      this.activeForecastKey = undefined;
      this.updateFeatures();
      return;
    }
    if (this.isEnergyForecastPortfolio()) {
      this.startEnergyForecastLoad();
      return;
    }
    const desiredKey = this.valueRequestKey(this.scene.getDisplayTimeWithOffset());
    if (desiredKey !== this.activeValueKey) {
      this.startValueLoad();
      return;
    }
    this.updateFeatures();
  }

  private valueRequestKey(time: DateTime) {
    if (!this.props.show || !this.portfolio || this.isEnergyForecastPortfolio()) {
      return undefined;
    }
    return portfolioEnergyValueRequestKey({
      portfolio: this.portfolio,
      requestFingerprint: this.requestFingerprint,
      model: this.props.model,
      time,
    });
  }

  private forecastRequestKey() {
    if (!this.props.show || !this.portfolio || !this.isEnergyForecastPortfolio()) {
      return undefined;
    }
    return portfolioEnergyForecastRequestKey({
      portfolio: this.portfolio,
      requestFingerprint: this.requestFingerprint,
      model: this.props.model,
    });
  }

  private isEnergyForecastPortfolio() {
    return this.portfolio?.dataSource === PortfolioDataSource.Energy;
  }

  private hasLoadableAssets() {
    return this.isEnergyForecastPortfolio()
      ? this.resolvedAssets.some((asset) => !asset.configurationIssue && asset.coordinates && asset.siteId)
      : this.resolvedAssets.some((asset) => asset.request);
  }

  private stopValueLoad() {
    this.valueLoad?.cancel();
    this.valueLoad = undefined;
    this.forecastLoad?.cancel();
    this.forecastLoad = undefined;
    reportLayerLoading(this.props.id, false);
  }

  private startValueLoad(time = this.scene.getDisplayTimeWithOffset()) {
    if (this.isEnergyForecastPortfolio()) {
      this.startEnergyForecastLoad(time);
      return;
    }
    const key = this.valueRequestKey(time);
    if (key === this.activeValueKey) {
      return;
    }

    this.stopValueLoad();
    this.values.clear();
    this.forecasts = new Map();
    this.activeForecastKey = undefined;
    this.activeValueKey = key;
    this.popover.remove();
    this.updateFeatures();

    const portfolio = this.portfolio;

    if (!key || !portfolio || !this.hasLoadableAssets()) {
      return;
    }

    const load = loadPortfolioEnergyValues({
      portfolio,
      assets: this.resolvedAssets,
      requestFingerprint: this.requestFingerprint,
      model: this.props.model,
      time,
    });
    this.valueLoad = load;
    this.activeValueKey = load.key;
    reportLayerLoading(this.props.id, true);
    void load.promise.then((values) => {
      if (this.valueLoad !== load || load.key !== this.activeValueKey) {
        return;
      }
      this.values = new Map(values);
      this.valueLoad = undefined;
      reportLayerLoading(this.props.id, false);
      this.updateFeatures();
    });
  }

  private startEnergyForecastLoad(time = this.scene.getDisplayTimeWithOffset()) {
    const key = this.forecastRequestKey();
    if (key === this.activeForecastKey) {
      if (this.forecasts.size) {
        this.values = selectPortfolioEnergyForecastValues(this.forecasts, time);
        this.updateFeatures();
      }
      return;
    }

    this.stopValueLoad();
    this.values.clear();
    this.forecasts = new Map();
    this.activeValueKey = undefined;
    this.activeForecastKey = key;
    this.popover.remove();
    this.updateFeatures();

    const portfolio = this.portfolio;
    if (!key || !portfolio || !this.hasLoadableAssets()) {
      return;
    }

    const load = loadPortfolioEnergyForecasts({
      portfolio,
      assets: this.resolvedAssets,
      requestFingerprint: this.requestFingerprint,
      model: this.props.model,
      time,
    });
    this.forecastLoad = load;
    this.activeForecastKey = load.key;
    reportLayerLoading(this.props.id, true);
    void load.promise.then((forecasts) => {
      if (this.forecastLoad !== load || load.key !== this.activeForecastKey) {
        return;
      }
      this.forecasts = forecasts;
      this.forecastLoad = undefined;
      reportLayerLoading(this.props.id, false);
      this.values = selectPortfolioEnergyForecastValues(this.forecasts, this.scene.getDisplayTimeWithOffset());
      this.updateFeatures();
    });
  }

  private updateFeatures() {
    const portfolio = this.portfolio;
    this.rowsByFeatureId.clear();
    this.rowsByAssetId.clear();
    if (!portfolio) {
      updateSourceData(this.scene.getMapboxMap(), this.sourceGroup.id("assets"), {
        type: "FeatureCollection",
        features: [],
      });
      return;
    }
    for (const asset of this.resolvedAssets) {
      const row = portfolio.assets[asset.rowIndex];
      if (asset.coordinates && row) {
        this.rowsByFeatureId.set(portfolioEnergyFeatureId(this.uid, portfolio.id, asset), row);
        if (asset.assetId) {
          this.rowsByAssetId.set(asset.assetId, row);
        }
      }
    }
    updateSourceData(
      this.scene.getMapboxMap(),
      this.sourceGroup.id("assets"),
      projectPortfolioEnergyAssets(this.uid, portfolio, this.resolvedAssets, this.values),
    );
  }

  private addEventListeners() {
    const map = this.scene.getMapboxMap();
    map.on("mouseenter", this.layerGroup.id("marker", "icon", "mw", "name"), this.handleMouseEnter);
    map.on("mouseleave", this.layerGroup.id("marker", "icon", "mw", "name"), this.handleMouseLeave);
    map.on("click", this.layerGroup.id("cluster"), this.handleClusterClick);
    map.on("click", this.layerGroup.id("marker", "icon", "mw", "name"), this.handleMarkerClick);
  }

  setInteractionsBlocked(blocked: boolean) {
    this.interactionsBlocked = blocked;
    if (blocked) {
      this.popover.remove();
      this.scene.getMapboxMap().getCanvas().style.cursor = "";
    }
  }

  private removeEventListeners() {
    const map = this.scene.getMapboxMap();
    map.off("mouseenter", this.layerGroup.id("marker", "icon", "mw", "name"), this.handleMouseEnter);
    map.off("mouseleave", this.layerGroup.id("marker", "icon", "mw", "name"), this.handleMouseLeave);
    map.off("click", this.layerGroup.id("cluster"), this.handleClusterClick);
    map.off("click", this.layerGroup.id("marker", "icon", "mw", "name"), this.handleMarkerClick);
  }

  private handleMouseEnter = () => {
    if (!this.interactionsBlocked) {
      this.scene.getMapboxMap().getCanvas().style.cursor = "pointer";
    }
  };

  private handleMouseLeave = () => {
    this.scene.getMapboxMap().getCanvas().style.cursor = "";
  };

  private handleClusterClick = (event: MapLayerMouseEvent) => {
    if (this.interactionsBlocked) {
      return;
    }
    const feature = event.features?.[0];
    if (feature?.geometry.type !== "Point") {
      return;
    }
    const coordinates = feature.geometry.coordinates as [number, number];
    const clusterId = feature.properties?.cluster_id;
    if (typeof clusterId !== "number") {
      return;
    }
    const source = this.scene.getMapboxMap().getSource(this.sourceGroup.id("assets")) as mapboxgl.GeoJSONSource;
    source.getClusterExpansionZoom(clusterId, (error, zoom) => {
      if (!error && typeof zoom === "number" && !this.interactionsBlocked) {
        this.scene.getMapboxMap().easeTo({ center: coordinates, zoom });
      }
    });
  };

  private handleMarkerClick = (event: MapLayerMouseEvent) => {
    if (this.interactionsBlocked || !this.portfolio) {
      return;
    }
    this.showAssetPopover(event);
  };

  private showAssetPopover(event: MapLayerMouseEvent) {
    if (this.interactionsBlocked || !this.portfolio) {
      return;
    }
    const features = (event.features ?? []) as MapboxGeoJSONFeature[];
    if (!features.length) {
      return;
    }
    const map = this.scene.getMapboxMap();
    const entries = new Map<string, { properties: PortfolioEnergyFeatureProperties; row: PortfolioRow }>();
    for (const feature of features) {
      const id = String(feature.id ?? "");
      const properties = feature.properties as PortfolioEnergyFeatureProperties | null;
      const row = this.rowsByFeatureId.get(id) ?? this.rowsByAssetId.get(properties?.assetId ?? "");
      if (row && properties) {
        entries.set(id || properties.assetId, { properties, row });
      }
    }
    if (!entries.size) {
      return;
    }
    const first = features.find(
      (feature): feature is MapboxGeoJSONFeature & { geometry: GeoJSON.Point } => feature.geometry.type === "Point",
    );
    if (!first) {
      return;
    }
    this.popover
      .setLngLat(first.geometry.coordinates as [number, number])
      .setHTML(createPortfolioEnergyPopover([...entries.values()]))
      .addTo(map);
  }

  checker(): PropsChecker<RenderablePortfolioEnergyPoiLayer, LayerBase<RenderablePortfolioEnergyPoiLayer>> {
    type Property = CheckedProps<RenderablePortfolioEnergyPoiLayer>;
    const check =
      (property: Property) =>
      (previous: RenderablePortfolioEnergyPoiLayer, current: RenderablePortfolioEnergyPoiLayer) => {
        if (previous[property] === current[property]) {
          return false;
        }
        const previousOptions = this.props.poiOptions;
        this.setLayerProps({ [property]: current[property] });
        if (property === "poiOptions") {
          const nextOptions = this.props.poiOptions;
          if (previousOptions.portfolioId !== nextOptions.portfolioId) {
            this.popover.remove();
          }
          this.setPortfolio(this.props.portfolio);
          this.rebuildMapboxContent();
        } else if (property === "portfolio") {
          this.setPortfolio(this.props.portfolio);
        } else if (property === "model") {
          this.startValueLoad();
        } else if (property === "show") {
          updateMapboxLayoutProperties(this.scene.getMapboxMap(), this.layerGroup.allIds(), {
            visibility: this.props.show ? "visible" : "none",
          });
          if (!this.props.show) {
            this.popover.remove();
          }
          this.startValueLoad();
        } else if (property === "opacity") {
          this.updateOpacity();
        }
        return true;
      };
    return {
      model: check("model"),
      calibrated: check("calibrated"),
      parameter_unit: check("parameter_unit"),
      poiOptions: check("poiOptions"),
      legend_visible: check("legend_visible"),
      opacity: check("opacity"),
      show: check("show"),
      ens_select: check("ens_select"),
      custom_options: check("custom_options"),
      portfolio: check("portfolio"),
    };
  }

  private updateOpacity() {
    const map = this.scene.getMapboxMap();
    const value = opacity(this.props);
    updateMapboxPaintProperties(map, this.layerGroup.id("cluster", "marker"), { "circle-opacity": value });
    updateMapboxPaintProperties(map, this.layerGroup.id("cluster-count", "mw", "name"), {
      "text-opacity": value,
    });
    updateMapboxPaintProperties(map, this.layerGroup.id("mw", "name"), { "icon-opacity": value });
    updateMapboxPaintProperties(map, this.layerGroup.id("icon"), { "icon-opacity": value });
  }

  get mapboxIndex() {
    return this.layerGroup.mainId();
  }

  moveZIndex(beforeLayerId?: string) {
    moveZIndex(this.scene.getMapboxMap(), this.layerGroup.allIds(), beforeLayerId);
  }

  getActiveWeatherParametersAsString() {
    return [...new Set(this.resolvedAssets.flatMap((asset) => (asset.request ? [asset.request.parameter] : [])))].map(
      (parameter) => ({ model: this.props.model, parameter }),
    );
  }

  isRebuffering(time: DateTime) {
    if (this.isEnergyForecastPortfolio()) {
      return !!this.forecastLoad && this.activeForecastKey === this.forecastRequestKey();
    }
    return !!this.valueLoad && this.activeValueKey === this.valueRequestKey(this.scene.getDateTimeWithOffset(time));
  }

  beforeRender() {
    this.startValueLoad();
  }

  fetchData(timeFrame: DateTime): VoidAsyncResult {
    const portfolio = this.portfolio;
    if (!this.props.show || !portfolio || !this.hasLoadableAssets()) {
      return createResolvedAsyncResult(undefined);
    }
    if (this.isEnergyForecastPortfolio()) {
      const load = loadPortfolioEnergyForecasts({
        portfolio,
        assets: this.resolvedAssets,
        requestFingerprint: this.requestFingerprint,
        model: this.props.model,
        time: this.scene.getDateTimeWithOffset(timeFrame),
      });
      return createPendingAsyncResult(
        load.promise.then(() => undefined),
        load.cancel,
      );
    }
    const load = loadPortfolioEnergyValues({
      portfolio,
      assets: this.resolvedAssets,
      requestFingerprint: this.requestFingerprint,
      model: this.props.model,
      time: this.scene.getDateTimeWithOffset(timeFrame),
    });
    return createPendingAsyncResult(
      load.promise.then(() => undefined),
      load.cancel,
    );
  }

  releaseLayer() {
    this.stopValueLoad();
    this.popover.remove();
    this.portfolio = undefined;
    this.resolvedAssets = [];
    this.requestFingerprint = "";
    this.values.clear();
    this.activeValueKey = undefined;
    this.forecasts = new Map();
    this.activeForecastKey = undefined;
    this.rowsByFeatureId.clear();
    this.rowsByAssetId.clear();
    clearLayerLoading(this.props.id);
  }

  removeLayer() {
    this.releaseLayer();
    this.removeEventListeners();
    this.scene.getMapboxMap().getCanvas().style.cursor = "";
    removeLayerGroup(this.scene.getMapboxMap(), this.layerGroup.allIds());
    removeSource(this.scene.getMapboxMap(), this.sourceGroup.allIds());
  }
}
